import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Row, Button } from "@masterteam/mt-design-react";

import EditForm from "./EditForm";
import NewTable from "../NewTable";
import CardList from "../CardList";
import AdminPage from "../AdminPage";
import DeleteModal from "../DeleteModal";

import api from "../../providers/api";
import { addToast } from "../../helpers";

import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete.svg";
import { getPerspectives, deletePerspective } from "../../reducers/perspective";
import AsyncSvg from "../AsyncSvg";

const initialModalState = {
  isOpen: false,
  data: null,
};

const PerspectivesPage = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isAr = intl.locale === "ar";
  const { strategies, isLoading } = useSelector(
    ({ perspective }) => perspective
  );

  const [formModal, setFormModal] = useState(initialModalState);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    dispatch(getPerspectives());
  }, []);

  const handleDelete = (id) => {
    setDeleteLoading(true);
    api.perspective
      .delete(id)
      .then((res) => {
        const { StatusCode, StatusMessage } = res;
        if (StatusCode === "Success") {
          dispatch(deletePerspective({ strategyIndex: selectedIndex, id }));
          setDeleteModal(initialModalState);
          addToast.success("toast.successDelete");
        } else if (StatusCode === "fail") {
          addToast.error(StatusMessage, {
            toastId: "get-users-error",
          });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  return (
    <AdminPage title={intl.formatMessage({ id: "common.perspectives" })}>
      <Row size="large">
        <div style={{ flex: 1 }}>
          <CardList
            showStrategyIcon
            data={strategies}
            title={intl.formatMessage({ id: "common.strategies" })}
            isLoading={isLoading}
            onClickItem={(item) => {
              setSelectedIndex(strategies?.findIndex((e) => e.ID === item.ID));
            }}
            nameKey={isAr ? "ArabicName" : "EnglishName"}
            valueKey="ID"
            selectedItem={strategies?.[selectedIndex]?.ID}
            animation={isAr ? "fadeInRight" : "fadeInLeft"}
          />
        </div>
        <div
          className="animated fadeInRight"
          style={{ flex: 2, height: "calc(100vh - 350px)" }}
        >
          <NewTable
            data={strategies[selectedIndex]?.Perspectives}
            isLoading={isLoading}
            actionsHeader={
              <Button
                fixed
                onClick={() => setFormModal({ isOpen: true, data: null })}
              >
                {intl.formatMessage({ id: "common.add" })}
              </Button>
            }
            columns={[
              {
                key: "Perspectives List",
                header: intl.formatMessage({ id: "common.perspectives" }),
                cell: (item) => (isAr ? item.ArabicName : item.EnglishName),
                width: "50%",
              },
              {
                key: "Icon",
                header: intl.formatMessage({ id: "common.icon" }),
                cell: (item) => (
                  <AsyncSvg
                    attachment={item?.Attachment}
                    color="var(--primary-color)"
                    style={{ width: 22, height: 22 }}
                  />
                ),
                width: "25%",
              },
              {
                key: "Order",
                header: intl.formatMessage({ id: "common.order" }),
                cell: (item) => item?.Order,
                width: "25%",
              },
            ]}
            actions={[
              {
                key: "edit",
                variant: "primary",
                Icon: EditIcon,
                "tooltip-position": "buttom",
                tooltip: intl.formatMessage({ id: "common.update" }),
                onClick: (item) => {
                  setFormModal({ isOpen: true, data: item });
                },
              },
              {
                key: "delete",
                variant: "error",
                Icon: DeleteIcon,
                "tooltip-position": "buttom",
                tooltip: intl.formatMessage({ id: "common.delete" }),
                onClick: (item) => {
                  setDeleteModal({ isOpen: true, data: item.ID });
                },
                disabled: (item) => !item.IsDeletable,
              },
            ]}
            compact
            scrollable
          />
        </div>
      </Row>

      {formModal.isOpen && (
        <EditForm
          data={formModal.data}
          selectedStrategy={strategies?.[selectedIndex]}
          closeModal={() => setFormModal(initialModalState)}
        />
      )}

      {deleteModal.isOpen && (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          title={intl.formatMessage({ id: "common.perspective" })}
          closeModal={() => setDeleteModal(initialModalState)}
          handleDelete={() => handleDelete(deleteModal.data)}
          ButtonStatus={deleteLoading}
        />
      )}
    </AdminPage>
  );
};

export default PerspectivesPage;
